import { apiClient } from './api';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

// ============ Interfaces ============

export interface Vendor {
  id: string;
  vendorNumber: string;
  companyLegalName: string;
  dbaName?: string;
  ein?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  website?: string;
  phone?: string;
  vendorType: string;
  industryCode?: string;
  riskTier: string;
  serviceCategories: string[];
  departmentsServed: string[];
  geographicRegions: string[];
  status: string;
  dateOnboarded?: string;
  primaryManagerId?: string;
  secondaryManagerId?: string;
  notes?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  contacts?: VendorContact[];
  _count?: { contracts: number };
}

export interface VendorContact {
  id: string;
  vendorId: string;
  fullName: string;
  jobTitle?: string;
  email: string;
  phone?: string;
  mobilePhone?: string;
  role: string;
  isPrimary: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Contract {
  id: string;
  contractNumber: string;
  title: string;
  contractType: string;
  parentContractId?: string;
  vendorId: string;
  businessOwnerId?: string;
  legalReviewerId?: string;
  contractAdminId?: string;
  contractValue?: number;
  currency: string;
  paymentTerms?: string;
  paymentFrequency?: string;
  annualValue?: number;
  budgetCode?: string;
  effectiveDate?: string;
  expirationDate?: string;
  executionDate?: string;
  noticePeriodDays?: number;
  noticeDeadline?: string;
  nextReviewDate?: string;
  isAutoRenewal: boolean;
  autoRenewalTermMonths?: number;
  maxRenewalCount?: number;
  renewalCountToDate: number;
  status: string;
  terminationReason?: string;
  governingLaw?: string;
  confidentialityLevel: string;
  description?: string;
  internalNotes?: string;
  tags: string[];
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  vendor?: { id: string; vendorNumber: string; companyLegalName: string; status: string };
  parentContract?: { id: string; contractNumber: string; title: string };
  childContracts?: { id: string; contractNumber: string; title: string; contractType: string; status: string }[];
  amendments?: ContractAmendment[];
  obligations?: ContractObligation[];
}

export interface ContractAmendment {
  id: string;
  contractId: string;
  amendmentNumber: string;
  title: string;
  effectiveDate?: string;
  description?: string;
  valueImpact: string;
  impactAmount?: number;
  runningTotal?: number;
  status: string;
  createdAt: string;
}

export interface ContractObligation {
  id: string;
  contractId: string;
  description: string;
  obligatedParty: string;
  category: string;
  dueDate?: string;
  frequency?: string;
  responsibleId?: string;
  status: string;
  evidenceNotes?: string;
  createdAt: string;
  contract?: { id: string; contractNumber: string; title: string; vendorId: string };
}

export interface VendorStatusHistory {
  id: string;
  vendorId: string;
  fromStatus: string;
  toStatus: string;
  changedBy: string;
  changedAt: string;
  comments?: string;
}

export interface VendorDocument {
  id: string;
  vendorId?: string;
  contractId?: string;
  fileName: string;
  displayName: string;
  category: string;
  description?: string;
  fileSize: number;
  mimeType: string;
  version: number;
  parentDocumentId?: string;
  expirationDate?: string;
  confidentialityLevel: string;
  uploadedBy?: string;
  changeNotes?: string;
  createdAt: string;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  resourceType: string;
  resourceId?: string;
  userId?: string;
  oldValues?: string;
  newValues?: string;
  createdAt: string;
}

export interface VendorStats {
  totalVendors: number;
  totalContracts: number;
  byStatus: Record<string, number>;
  byRiskTier: Record<string, number>;
}

export interface DuplicateResult {
  vendor: Partial<Vendor>;
  matchReasons: string[];
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AutoRenewalDashboard {
  actionRequired: Contract[];
  approaching: Contract[];
  upcoming: Contract[];
  totalAutoRenewal: number;
  summary: { actionRequiredCount: number; approachingCount: number; upcomingCount: number };
}

export interface VendorAnalytics {
  vendors: { total: number; byStatus: Record<string, number>; byType: Record<string, number>; byRiskTier: Record<string, number> };
  contracts: { total: number; byStatus: Record<string, number>; byType: Record<string, number>; expiringIn30Days: number; expiringIn90Days: number; totalValue: number; autoRenewalCount: number };
  obligations: { overdueCount: number };
  amendments: { total: number };
  documents: { total: number; expiringIn30Days: number };
}

export interface ContractRequest {
  id: string;
  requestNumber: string;
  requestorName: string;
  requestorEmail: string;
  dateReceived: string;
  source: string;
  requestType: string;
  vendorId?: string;
  vendorName?: string;
  description?: string;
  urgency: string;
  assignedHandler?: string;
  status: string;
  targetCompletionDate?: string;
  actualCompletionDate?: string;
  relatedContractId?: string;
  notes?: string;
  createdAt: string;
  vendor?: { id: string; vendorNumber: string; companyLegalName: string };
}

export interface ContractRequestStats {
  total: number;
  byStatus: Record<string, number>;
  byUrgency: Record<string, number>;
  byRequestType: Record<string, number>;
  overdueCount: number;
}

export interface GetVendorsParams {
  page?: number;
  limit?: number;
  status?: string;
  vendorType?: string;
  riskTier?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetContractsParams {
  page?: number;
  limit?: number;
  status?: string;
  contractType?: string;
  vendorId?: string;
  isAutoRenewal?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============ Display Helpers ============

const VENDOR_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  PROSPECT: { label: 'Prospect', color: 'secondary' },
  UNDER_REVIEW: { label: 'Under Review', color: 'warning' },
  APPROVED: { label: 'Approved', color: 'success' },
  ACTIVE: { label: 'Active', color: 'success' },
  UNDER_REMEDIATION: { label: 'Under Remediation', color: 'warning' },
  SUSPENDED: { label: 'Suspended', color: 'danger' },
  TERMINATED: { label: 'Terminated', color: 'danger' },
  ARCHIVED: { label: 'Archived', color: 'secondary' },
};

const VENDOR_TYPE_CONFIG: Record<string, string> = {
  SUPPLIER: 'Supplier',
  CONTRACTOR: 'Contractor',
  CONSULTANT: 'Consultant',
  TECHNOLOGY_PROVIDER: 'Technology Provider',
  PROFESSIONAL_SERVICES: 'Professional Services',
  FINANCIAL_SERVICES: 'Financial Services',
  FACILITIES: 'Facilities',
  OTHER: 'Other',
};

const RISK_TIER_CONFIG: Record<string, { label: string; color: string }> = {
  CRITICAL: { label: 'Critical', color: 'danger' },
  HIGH: { label: 'High', color: 'warning' },
  MEDIUM: { label: 'Medium', color: 'secondary' },
  LOW: { label: 'Low', color: 'success' },
};

const CONTRACT_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'secondary' },
  UNDER_REVIEW: { label: 'Under Review', color: 'warning' },
  PENDING_APPROVAL: { label: 'Pending Approval', color: 'warning' },
  ACTIVE: { label: 'Active', color: 'success' },
  EXPIRING_SOON: { label: 'Expiring Soon', color: 'danger' },
  EXPIRED: { label: 'Expired', color: 'danger' },
  RENEWED: { label: 'Renewed', color: 'success' },
  TERMINATED: { label: 'Terminated', color: 'danger' },
  ARCHIVED: { label: 'Archived', color: 'secondary' },
};

const CONTRACT_TYPE_CONFIG: Record<string, string> = {
  MSA: 'Master Service Agreement',
  SOW: 'Statement of Work',
  NDA: 'Non-Disclosure Agreement',
  SLA: 'Service Level Agreement',
  SOFTWARE_LICENSE: 'Software License',
  LEASE: 'Lease Agreement',
  AMENDMENT: 'Amendment',
  PURCHASE_ORDER: 'Purchase Order',
  CONSULTING: 'Consulting Agreement',
  OTHER: 'Other',
};

const REQUEST_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  RECEIVED: { label: 'Received', color: 'secondary' },
  IN_REVIEW: { label: 'In Review', color: 'warning' },
  IN_PROGRESS: { label: 'In Progress', color: 'primary' },
  PENDING_SIGNATURES: { label: 'Pending Signatures', color: 'warning' },
  COMPLETE: { label: 'Complete', color: 'success' },
};

const REQUEST_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  NEW_CONTRACT: { label: 'New Contract', color: 'primary' },
  RENEWAL: { label: 'Renewal', color: 'success' },
  AMENDMENT: { label: 'Amendment', color: 'warning' },
  REVIEW: { label: 'Review', color: 'secondary' },
  NDA_REQUEST: { label: 'NDA Request', color: 'primary' },
  RFP_SUPPORT: { label: 'RFP Support', color: 'secondary' },
  OTHER: { label: 'Other', color: 'secondary' },
};

const URGENCY_CONFIG: Record<string, { label: string; color: string }> = {
  STANDARD: { label: 'Standard', color: 'secondary' },
  EXPEDITED: { label: 'Expedited', color: 'warning' },
  URGENT: { label: 'Urgent', color: 'danger' },
};

// ============ Service Class ============

class VendorService {
  // --- Vendor CRUD ---

  async getVendors(params: GetVendorsParams = {}): Promise<{ vendors: Vendor[]; pagination: Pagination }> {
    const queryParams = new URLSearchParams();
    if (params.page) {queryParams.append('page', params.page.toString());}
    if (params.limit) {queryParams.append('limit', params.limit.toString());}
    if (params.status && params.status !== 'all') {queryParams.append('status', params.status);}
    if (params.vendorType && params.vendorType !== 'all') {queryParams.append('vendorType', params.vendorType);}
    if (params.riskTier && params.riskTier !== 'all') {queryParams.append('riskTier', params.riskTier);}
    if (params.search) {queryParams.append('search', params.search);}
    if (params.sortBy) {queryParams.append('sortBy', params.sortBy);}
    if (params.sortOrder) {queryParams.append('sortOrder', params.sortOrder);}

    const url = `/api/v1/legal/vendors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<{ vendors: Vendor[]; pagination: Pagination }>(url);
    if (response.success && response.data) {
      return {
        vendors: response.data.vendors ?? [],
        pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch vendors');
  }

  async getVendor(id: string): Promise<Vendor> {
    const response = await apiClient.get<Vendor>(`/api/v1/legal/vendors/${id}`);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to fetch vendor');
  }

  async createVendor(data: Partial<Vendor>): Promise<Vendor> {
    const response = await apiClient.post<Vendor>('/api/v1/legal/vendors', data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to create vendor');
  }

  async updateVendor(id: string, data: Partial<Vendor>): Promise<Vendor> {
    const response = await apiClient.put<Vendor>(`/api/v1/legal/vendors/${id}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update vendor');
  }

  async deleteVendor(id: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/legal/vendors/${id}`);
    if (!response.success) {throw new Error(response.error?.message ?? 'Failed to delete vendor');}
  }

  async updateVendorStatus(id: string, status: string, comments?: string): Promise<Vendor> {
    const response = await apiClient.put<Vendor>(`/api/v1/legal/vendors/${id}/status`, { status, comments });
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update vendor status');
  }

  async checkDuplicates(data: { companyLegalName: string; dbaName?: string; ein?: string; website?: string }): Promise<{ duplicates: DuplicateResult[]; hasPotentialDuplicates: boolean }> {
    const response = await apiClient.post<{ duplicates: DuplicateResult[]; hasPotentialDuplicates: boolean }>('/api/v1/legal/vendors/check-duplicates', data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to check duplicates');
  }

  // --- Vendor Contacts ---

  async getVendorContacts(vendorId: string): Promise<VendorContact[]> {
    const response = await apiClient.get<{ contacts: VendorContact[] }>(`/api/v1/legal/vendors/${vendorId}/contacts`);
    if (response.success && response.data) {return response.data.contacts ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch contacts');
  }

  async createVendorContact(vendorId: string, data: Partial<VendorContact>): Promise<VendorContact> {
    const response = await apiClient.post<VendorContact>(`/api/v1/legal/vendors/${vendorId}/contacts`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to create contact');
  }

  async updateVendorContact(vendorId: string, contactId: string, data: Partial<VendorContact>): Promise<VendorContact> {
    const response = await apiClient.put<VendorContact>(`/api/v1/legal/vendors/${vendorId}/contacts/${contactId}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update contact');
  }

  async deleteVendorContact(vendorId: string, contactId: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/legal/vendors/${vendorId}/contacts/${contactId}`);
    if (!response.success) {throw new Error(response.error?.message ?? 'Failed to delete contact');}
  }

  // --- Vendor History ---

  async getVendorAuditLog(vendorId: string, page: number = 1): Promise<{ entries: AuditLogEntry[]; pagination: Pagination }> {
    const response = await apiClient.get<{ entries: AuditLogEntry[]; pagination: Pagination }>(`/api/v1/legal/vendors/${vendorId}/audit-log?page=${page}&limit=50`);
    if (response.success && response.data) {
      return { entries: response.data.entries ?? [], pagination: response.data.pagination ?? { total: 0, page: 1, limit: 50, totalPages: 0 } };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch vendor audit log');
  }

  async getVendorHistory(vendorId: string): Promise<VendorStatusHistory[]> {
    const response = await apiClient.get<{ history: VendorStatusHistory[] }>(`/api/v1/legal/vendors/${vendorId}/history`);
    if (response.success && response.data) {return response.data.history ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch history');
  }

  // --- Contracts ---

  async getContracts(params: GetContractsParams = {}): Promise<{ contracts: Contract[]; pagination: Pagination }> {
    const queryParams = new URLSearchParams();
    if (params.page) {queryParams.append('page', params.page.toString());}
    if (params.limit) {queryParams.append('limit', params.limit.toString());}
    if (params.status && params.status !== 'all') {queryParams.append('status', params.status);}
    if (params.contractType && params.contractType !== 'all') {queryParams.append('contractType', params.contractType);}
    if (params.vendorId) {queryParams.append('vendorId', params.vendorId);}
    if (params.search) {queryParams.append('search', params.search);}
    if (params.sortBy) {queryParams.append('sortBy', params.sortBy);}
    if (params.sortOrder) {queryParams.append('sortOrder', params.sortOrder);}

    const url = `/api/v1/legal/contracts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<{ contracts: Contract[]; pagination: Pagination }>(url);
    if (response.success && response.data) {
      return {
        contracts: response.data.contracts ?? [],
        pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch contracts');
  }

  async getContract(id: string): Promise<Contract> {
    const response = await apiClient.get<Contract>(`/api/v1/legal/contracts/${id}`);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to fetch contract');
  }

  async createContract(data: Partial<Contract>): Promise<Contract> {
    const response = await apiClient.post<Contract>('/api/v1/legal/contracts', data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to create contract');
  }

  async updateContract(id: string, data: Partial<Contract>): Promise<Contract> {
    const response = await apiClient.put<Contract>(`/api/v1/legal/contracts/${id}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update contract');
  }

  async deleteContract(id: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/legal/contracts/${id}`);
    if (!response.success) {throw new Error(response.error?.message ?? 'Failed to delete contract');}
  }

  async updateContractStatus(id: string, status: string, comments?: string): Promise<Contract> {
    const response = await apiClient.put<{ contract: Contract }>(`/api/v1/legal/contracts/${id}/status`, { status, comments });
    if (response.success && response.data) {return response.data.contract;}
    throw new Error(response.error?.message ?? 'Failed to update contract status');
  }

  async getVendorContracts(vendorId: string): Promise<{ contracts: Contract[]; pagination: Pagination }> {
    const response = await apiClient.get<{ contracts: Contract[]; pagination: Pagination }>(`/api/v1/legal/vendors/${vendorId}/contracts`);
    if (response.success && response.data) {
      return {
        contracts: response.data.contracts ?? [],
        pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch vendor contracts');
  }

  // --- Amendments ---

  async getAmendments(contractId: string): Promise<ContractAmendment[]> {
    const response = await apiClient.get<{ amendments: ContractAmendment[] }>(`/api/v1/legal/contracts/${contractId}/amendments`);
    if (response.success && response.data) {return response.data.amendments ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch amendments');
  }

  async createAmendment(contractId: string, data: Partial<ContractAmendment>): Promise<ContractAmendment> {
    const response = await apiClient.post<ContractAmendment>(`/api/v1/legal/contracts/${contractId}/amendments`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to create amendment');
  }

  async updateAmendment(contractId: string, amendmentId: string, data: Partial<ContractAmendment>): Promise<ContractAmendment> {
    const response = await apiClient.put<ContractAmendment>(`/api/v1/legal/contracts/${contractId}/amendments/${amendmentId}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update amendment');
  }

  async deleteAmendment(contractId: string, amendmentId: string): Promise<void> {
    const response = await apiClient.delete(`/api/v1/legal/contracts/${contractId}/amendments/${amendmentId}`);
    if (!response.success) {throw new Error(response.error?.message ?? 'Failed to delete amendment');}
  }

  // --- Obligations ---

  async getObligations(contractId: string): Promise<ContractObligation[]> {
    const response = await apiClient.get<{ obligations: ContractObligation[] }>(`/api/v1/legal/contracts/${contractId}/obligations`);
    if (response.success && response.data) {return response.data.obligations ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch obligations');
  }

  async createObligation(contractId: string, data: Partial<ContractObligation>): Promise<ContractObligation> {
    const response = await apiClient.post<ContractObligation>(`/api/v1/legal/contracts/${contractId}/obligations`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to create obligation');
  }

  async updateObligation(contractId: string, obligationId: string, data: Partial<ContractObligation>): Promise<ContractObligation> {
    const response = await apiClient.put<ContractObligation>(`/api/v1/legal/contracts/${contractId}/obligations/${obligationId}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update obligation');
  }

  async deleteObligation(contractId: string, obligationId: string): Promise<void> {
    const response = await apiClient.delete(`/api/v1/legal/contracts/${contractId}/obligations/${obligationId}`);
    if (!response.success) {throw new Error(response.error?.message ?? 'Failed to delete obligation');}
  }

  // --- Documents ---

  async getVendorDocuments(vendorId: string): Promise<VendorDocument[]> {
    const response = await apiClient.get<{ documents: VendorDocument[] }>(`/api/v1/legal/vendors/${vendorId}/documents`);
    if (response.success && response.data) {return response.data.documents ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch documents');
  }

  async uploadVendorDocument(
    vendorId: string,
    file: File,
    metadata: { displayName?: string; category?: string; description?: string; expirationDate?: string; confidentialityLevel?: string },
    onProgress?: (progress: number) => void,
  ): Promise<VendorDocument> {
    const response = await apiClient.uploadFile<VendorDocument>(
      `/api/v1/legal/vendors/${vendorId}/documents`,
      file,
      onProgress,
      metadata,
    );
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to upload document');
  }

  async getContractDocuments(contractId: string): Promise<VendorDocument[]> {
    const response = await apiClient.get<{ documents: VendorDocument[] }>(`/api/v1/legal/contracts/${contractId}/documents`);
    if (response.success && response.data) {return response.data.documents ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch documents');
  }

  async uploadContractDocument(
    contractId: string,
    file: File,
    metadata: { displayName?: string; category?: string; description?: string; expirationDate?: string; confidentialityLevel?: string },
    onProgress?: (progress: number) => void,
  ): Promise<VendorDocument> {
    const response = await apiClient.uploadFile<VendorDocument>(
      `/api/v1/legal/contracts/${contractId}/documents`,
      file,
      onProgress,
      metadata,
    );
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to upload document');
  }

  async downloadDocument(documentId: string, filename?: string): Promise<void> {
    await apiClient.downloadFile(`/api/v1/legal/documents/${documentId}/download`, filename);
  }

  async updateDocument(documentId: string, data: { displayName?: string; category?: string; description?: string; expirationDate?: string; confidentialityLevel?: string }): Promise<VendorDocument> {
    const response = await apiClient.put<VendorDocument>(`/api/v1/legal/documents/${documentId}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update document');
  }

  async deleteDocument(documentId: string): Promise<void> {
    const response = await apiClient.delete(`/api/v1/legal/documents/${documentId}`);
    if (!response.success) {throw new Error(response.error?.message ?? 'Failed to delete document');}
  }

  async uploadDocumentVersion(
    documentId: string,
    file: File,
    changeNotes?: string,
    onProgress?: (progress: number) => void,
  ): Promise<VendorDocument> {
    const response = await apiClient.uploadFile<VendorDocument>(
      `/api/v1/legal/documents/${documentId}/versions`,
      file,
      onProgress,
      changeNotes ? { changeNotes } : undefined,
    );
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to upload document version');
  }

  async getDocumentVersions(documentId: string): Promise<VendorDocument[]> {
    const response = await apiClient.get<{ versions: VendorDocument[] }>(`/api/v1/legal/documents/${documentId}/versions`);
    if (response.success && response.data) {return response.data.versions ?? [];}
    throw new Error(response.error?.message ?? 'Failed to fetch document versions');
  }

  // --- Audit Log ---

  async getContractAuditLog(contractId: string, page: number = 1): Promise<{ entries: AuditLogEntry[]; pagination: Pagination }> {
    const response = await apiClient.get<{ entries: AuditLogEntry[]; pagination: Pagination }>(`/api/v1/legal/contracts/${contractId}/audit-log?page=${page}&limit=50`);
    if (response.success && response.data) {
      return { entries: response.data.entries ?? [], pagination: response.data.pagination ?? { total: 0, page: 1, limit: 50, totalPages: 0 } };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch audit log');
  }

  // --- Display Helpers ---

  getVendorStatusConfig(status: string): { label: string; color: string } {
    return VENDOR_STATUS_CONFIG[status] ?? { label: status, color: 'secondary' };
  }

  getVendorTypeLabel(type: string): string {
    return VENDOR_TYPE_CONFIG[type] ?? type;
  }

  getRiskTierConfig(tier: string): { label: string; color: string } {
    return RISK_TIER_CONFIG[tier] ?? { label: tier, color: 'secondary' };
  }

  getContractStatusConfig(status: string): { label: string; color: string } {
    return CONTRACT_STATUS_CONFIG[status] ?? { label: status, color: 'secondary' };
  }

  getContractTypeLabel(type: string): string {
    return CONTRACT_TYPE_CONFIG[type] ?? type;
  }

  getRequestStatusConfig(status: string): { label: string; color: string } {
    return REQUEST_STATUS_CONFIG[status] ?? { label: status, color: 'secondary' };
  }

  getRequestTypeLabel(type: string): { label: string; color: string } {
    return REQUEST_TYPE_CONFIG[type] ?? { label: type, color: 'secondary' };
  }

  getUrgencyConfig(urgency: string): { label: string; color: string } {
    return URGENCY_CONFIG[urgency] ?? { label: urgency, color: 'secondary' };
  }

  formatCurrency(value: number | undefined | null, currency: string = 'USD'): string {
    if (value === null || value === undefined) {return '-';}
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  }

  formatDate(dateStr: string | undefined | null): string {
    if (!dateStr) {return '-';}
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  formatTimestamp(dateStr: string | undefined | null): string {
    if (!dateStr) {return '-';}
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  // --- Auto-Renewal & Analytics ---

  async getAutoRenewalDashboard(): Promise<AutoRenewalDashboard> {
    const response = await apiClient.get<AutoRenewalDashboard>('/api/v1/legal/reporting/auto-renewals');
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to fetch auto-renewal dashboard');
  }

  async getVendorAnalytics(): Promise<VendorAnalytics> {
    const response = await apiClient.get<VendorAnalytics>('/api/v1/legal/reporting/analytics');
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to fetch analytics');
  }

  // --- Contract Requests ---

  async getContractRequests(params: { page?: number; limit?: number; status?: string; requestType?: string; urgency?: string; search?: string } = {}): Promise<{ requests: ContractRequest[]; pagination: Pagination }> {
    const queryParams = new URLSearchParams();
    if (params.page) {queryParams.append('page', params.page.toString());}
    if (params.limit) {queryParams.append('limit', params.limit.toString());}
    if (params.status && params.status !== 'all') {queryParams.append('status', params.status);}
    if (params.requestType && params.requestType !== 'all') {queryParams.append('requestType', params.requestType);}
    if (params.urgency && params.urgency !== 'all') {queryParams.append('urgency', params.urgency);}
    if (params.search) {queryParams.append('search', params.search);}
    const url = `/api/v1/legal/contract-requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<{ requests: ContractRequest[]; pagination: Pagination }>(url);
    if (response.success && response.data) {
      return { requests: response.data.requests ?? [], pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 } };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch contract requests');
  }

  async getContractRequestStats(): Promise<ContractRequestStats> {
    const response = await apiClient.get<ContractRequestStats>('/api/v1/legal/contract-requests/stats');
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to fetch request stats');
  }

  async createContractRequest(data: Partial<ContractRequest>): Promise<ContractRequest> {
    const response = await apiClient.post<ContractRequest>('/api/v1/legal/contract-requests', data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to create contract request');
  }

  async updateContractRequest(id: string, data: Partial<ContractRequest>): Promise<ContractRequest> {
    const response = await apiClient.put<ContractRequest>(`/api/v1/legal/contract-requests/${id}`, data);
    if (response.success && response.data) {return response.data;}
    throw new Error(response.error?.message ?? 'Failed to update contract request');
  }
}

export const vendorService = new VendorService();
